/**
 * Правило «дубль названий юридических документов» — ПУБЛИЧНАЯ ЧАСТЬ.
 *
 * Правда о названиях одна — `backend/src/ai-funnels/legal-doc-names.registry.ts`,
 * её зеркало для кабинета — `legalDocNames.ts`. Здесь лежит то, и только то,
 * что едет В БРАУЗЕР ПОКУПАТЕЛЯ ПЕРВЫМ ЭКРАНОМ.
 *
 * РЕШЕНИЕ ВЛАДЕЛЬЦА 23.09.2026, вариант В (вопрос №5 о весе публичного входа):
 * правило работает на публике НА ВСЕХ языках генерации, но первый экран от
 * этого не растёт. Полный реестр на 29 языков стоил бы 2,3 КБ по проводу сверх
 * храповика, поэтому язык воронки приезжает ЛЕНИВО — вместе с языковым пакетом
 * надписей воронки (WEB-2057), в том же файле и тем же ожиданием: страница
 * воронки и страница оплаты уже не рисуют содержимое, пока пакет не на руках
 * (`loadPublicQuizStrings`, `stringsState` в `PublicQuiz.tsx`/`PublicPaywall.tsx`).
 * Второго механизма ожидания не заведено намеренно — он был бы вторым концом
 * одного правила и разъехался бы с первым.
 *
 * ЧТО ЗДЕСЬ ЛЕЖИТ. Английский и русский — ядро: их выражения предвычислены и
 * всегда на руках, ровно как `CORE_STRINGS` у надписей. Остальные языки
 * приносит пакет полем `legalDocPatterns`, и он же кладёт их сюда
 * (`registerLegalDocPatternsSource`). Пакет не доехал (сеть, унесло выкатом) —
 * работают английский и русский, а не пустота и не вечное ожидание: сбой это не
 * отсутствие (шрам WEB-1921), а прежнее поведение до WEB-2384.
 *
 * ПОЧЕМУ ВЫРАЖЕНИЯ ПРЕДВЫЧИСЛЕНЫ, а не собраны из реестра. Механизм — данные,
 * разбор компактной записи, сборка выражений, кэш — стоил 149 байт сверх
 * потолка сам по себе, без единой строки данных. В браузере покупателя он не
 * нужен: набор языков там не меняется, значит и выражения не меняются. Поэтому
 * и ядро, и пакеты несут готовые строки, а собирает их по-прежнему ОДНА формула
 * — та, что в реестре. Сторож
 * `backend/src/shared/guards/legal-doc-names-ends.web2384.spec.ts` сверяет
 * КАЖДУЮ такую строку с собранной посимвольно, поэтому второй копией ПРАВИЛА
 * это не становится.
 *
 * КАБИНЕТ ходит мимо этого механизма: `src/main.tsx` подменяет источник
 * выражений полным сборщиком (`installLegalDocPatterns`), потому что там автор
 * переключает язык на лету и ждать нечего.
 */

export interface LegalDocName {
  /** Название документа как его пишут. */
  readonly text: string;
  /** Образец распознавания (regex source). По умолчанию выводится из text. */
  readonly pattern?: string;
}

export interface LegalDocKindNames {
  /** Однозначное название документа целиком — повод резать. */
  readonly full: readonly LegalDocName[];
  /** Голая форма — сама по себе НЕ повод, только внутри цепочки. */
  readonly bare: readonly LegalDocName[];
}

export interface LegalDocLanguage {
  readonly terms: LegalDocKindNames;
  readonly privacy: LegalDocKindNames;
  /** Соединители между документами: «Terms of Use AND Privacy Policy». */
  readonly connectors: readonly string[];
}

export interface LegalDocPatterns {
  /** Цепочка названий документов: названия и соединители, без авторского текста. */
  readonly docRun: RegExp;
  /** Есть ли в найденном куске ОДНОЗНАЧНОЕ название (иначе не режем). */
  readonly hasAnchor: RegExp;
  /** Какой именно документ в куске: группа 1 — условия, группа 2 — приватность. */
  readonly taggedDocName: RegExp;
}

/**
 * Выражения ядра — английский и русский, собранные ТОЙ ЖЕ формулой реестра и
 * записанные сюда готовыми. Отдельными именованными константами не вынесены
 * намеренно: каждое имя в первом экране платное, а сторож всё равно читает эти
 * строки из исходника с диска и сверяет с собранными посимвольно.
 */
/*
 * Правило `no-misleading-character-class` отключено ОСОЗНАННО. Оно
 * предупреждает о классах символов, куда попадают комбинирующие знаки, — а у
 * нас такой класс один и намеренный: «граница слова», где перечислены
 * письменности языков генерации. Диапазоны иврита и деванагари включают свои
 * комбинирующие знаки, и это ВЕРНО: они часть слова, а не разделитель между
 * словами, иначе название документа в ивритской воронке матчилось бы посреди
 * слова.
 */
/* eslint-disable no-misleading-character-class */
const PUBLIC_PATTERNS: LegalDocPatterns = {
  docRun: new RegExp('(^|[^A-Za-z0-9\\u00C0-\\u024F\\u0370-\\u03FF\\u0400-\\u04FF\\u0590-\\u05FF\\u0600-\\u06FF\\u0900-\\u097F\\u1E00-\\u1EFF])((?:(?:политик[а-яё]*\\s+конфиденциальност[а-яё]*|пользовательск[а-яё]*\\s+соглашени[а-яё]*|услови[а-яё]*\\s+использования|Terms\\s+and\\s+Conditions|Terms\\s+of\\s+Service|Terms\\s+of\\s+Use|Privacy\\s+Policy)|(?:конфиденциальност[а-яё]*|политик[а-яё]*|услови[а-яё]*|Privacy|Terms))(?:(?:\\s*(?:and|&|и)\\s*)(?:(?:политик[а-яё]*\\s+конфиденциальност[а-яё]*|пользовательск[а-яё]*\\s+соглашени[а-яё]*|услови[а-яё]*\\s+использования|Terms\\s+and\\s+Conditions|Terms\\s+of\\s+Service|Terms\\s+of\\s+Use|Privacy\\s+Policy)|(?:конфиденциальност[а-яё]*|политик[а-яё]*|услови[а-яё]*|Privacy|Terms)))*)(?![A-Za-z0-9\\u00C0-\\u024F\\u0370-\\u03FF\\u0400-\\u04FF\\u0590-\\u05FF\\u0600-\\u06FF\\u0900-\\u097F\\u1E00-\\u1EFF])', 'gi'), // i18n-allow: образцы РАСПОЗНАВАНИЯ авторского текста воронки (её язык), не надписи кабинета
  hasAnchor: new RegExp('(?:политик[а-яё]*\\s+конфиденциальност[а-яё]*|пользовательск[а-яё]*\\s+соглашени[а-яё]*|услови[а-яё]*\\s+использования|Terms\\s+and\\s+Conditions|Terms\\s+of\\s+Service|Terms\\s+of\\s+Use|Privacy\\s+Policy)', 'i'), // i18n-allow: образцы РАСПОЗНАВАНИЯ авторского текста воронки (её язык), не надписи кабинета
  taggedDocName: new RegExp('(пользовательск[а-яё]*\\s+соглашени[а-яё]*|услови[а-яё]*\\s+использования|Terms\\s+and\\s+Conditions|Terms\\s+of\\s+Service|Terms\\s+of\\s+Use|услови[а-яё]*|Terms)|(политик[а-яё]*\\s+конфиденциальност[а-яё]*|конфиденциальност[а-яё]*|Privacy\\s+Policy|политик[а-яё]*|Privacy)', 'gi'), // i18n-allow: образцы РАСПОЗНАВАНИЯ авторского текста воронки (её язык), не надписи кабинета
};
/* eslint-enable no-misleading-character-class */
 

/** Три исходника выражений так, как их несёт языковой пакет. */
export interface LegalDocPatternSources {
  readonly docRun: string;
  readonly hasAnchor: string;
  readonly taggedDocName: string;
}

/**
 * Выражения языка, чей пакет доехал. ОДНА ячейка, а не таблица: публичная
 * страница показывает ровно одну воронку, то есть ровно один язык, и таблица
 * была бы платой за то, чего в браузере покупателя не бывает (первый экран под
 * храповиком веса).
 */
let packLanguage = '';
let packPatterns: LegalDocPatterns | null = null;

/** Базовый код языка: «pt-BR» и «pt_BR» — это «pt». */
function baseCode(language?: string | null): string {
  return (language ?? '').trim().toLowerCase().replace(/_/g, '-').split('-')[0];
}

 
/**
 * Принять выражения из языкового пакета. Зовёт `publicQuizStrings` в тот же
 * миг, когда раскладывает надписи. Пустой язык — сброс (нужен тестам: память
 * модуля переживает границу случая, шрам WEB-2359).
 */
export function registerLegalDocPatternsSource(
  language: string,
  sources?: LegalDocPatternSources,
): void {
  if (!sources) {
    if (language === '') {
      packLanguage = '';
      packPatterns = null;
    }
    return;
  }
  packLanguage = baseCode(language);
  packPatterns = {
    docRun: new RegExp(sources.docRun, 'gi'),
    hasAnchor: new RegExp(sources.hasAnchor, 'i'),
    taggedDocName: new RegExp(sources.taggedDocName, 'gi'),
  };
}
 

/**
 * Источник выражений. По умолчанию — язык из приехавшего пакета, иначе ядро
 * (английский и русский). Кабинет подменяет источник целиком, импортируя
 * `./legalDocNames` со своего входа (`src/main.tsx`).
 */
let resolvePatterns: (language?: string | null) => LegalDocPatterns = (
  language,
) =>
  packPatterns !== null && baseCode(language) === packLanguage
    ? packPatterns
    : PUBLIC_PATTERNS;

/** Подмена источника выражений на полный набор языков. Зовёт `legalDocNames.ts`. */
export function installLegalDocPatterns(
  resolver: (language?: string | null) => LegalDocPatterns,
): void {
  resolvePatterns = resolver;
}

/** Выражения для языка воронки: на публике — публичный набор, в кабинете — полный. */
export function legalDocPatterns(
  language?: string | null,
): LegalDocPatterns {
  return resolvePatterns(language);
}
